import { OwnerSquad, ScreenName } from '@/analytics/generated/segment'
import { sendAnalyticsEvent } from '@/analytics/segmentClient'
import type { GuideTourNotificationInteractionEvent } from '@/guide-tour/notifications/types'
import { PRACTICE_ORDER_EDIT_TOUR_ID } from '@/components/business/EditOrderModal/PracticeEdit/components/PracticeOrderGuideTourButton/utils/utils'
import { EDIT_ORDER as dict } from '@/resources/dict'
import { pathNameToSegmentScreenName } from '@/resources/utils/analytics'

const practiceAnalyticsDict = dict.practiceOrderEdit.analytics

export const PRACTICE_ORDER_EDIT_GUIDE_TOUR_SEGMENT_EVENTS = {
  stepViewed: 'Order Edit Guide Tour Step Viewed',
  stepCompleted: 'Order Edit Guide Tour Step Completed',
  closed: 'Order Edit Guide Tour Closed',
} as const

type PracticeOrderEditGuideTourTrackableAction =
  | 'start'
  | 'viewed'
  | 'next'
  | 'complete'
  | 'dismiss'
  | 'previous'

const isPracticeOrderEditGuideTour = (
  event: GuideTourNotificationInteractionEvent
): boolean =>
  event.tourId === PRACTICE_ORDER_EDIT_TOUR_ID ||
  event.tour?.id === PRACTICE_ORDER_EDIT_TOUR_ID

export const getPracticeOrderEditGuideTourTrackableEvent = (
  event: GuideTourNotificationInteractionEvent
): {
  eventName: string
  component_name?: string
} | null => {
  const { action } = event

  switch (action) {
    case 'start':
    case 'viewed':
      return {
        eventName: PRACTICE_ORDER_EDIT_GUIDE_TOUR_SEGMENT_EVENTS.stepViewed,
      }

    case 'next':
      return {
        eventName: PRACTICE_ORDER_EDIT_GUIDE_TOUR_SEGMENT_EVENTS.stepCompleted,
        component_name: practiceAnalyticsDict.componentName.guideTourNextStep,
      }

    case 'complete':
      return {
        eventName: PRACTICE_ORDER_EDIT_GUIDE_TOUR_SEGMENT_EVENTS.stepCompleted,
        component_name: practiceAnalyticsDict.componentName.guideTourClose,
      }

    case 'dismiss':
      return {
        eventName: PRACTICE_ORDER_EDIT_GUIDE_TOUR_SEGMENT_EVENTS.closed,
        component_name: practiceAnalyticsDict.componentName.guideTourDismiss,
      }

    case 'previous':
      return {
        eventName: PRACTICE_ORDER_EDIT_GUIDE_TOUR_SEGMENT_EVENTS.closed,
        component_name: practiceAnalyticsDict.componentName.guideTourBack,
      }

    default:
      return null
  }
}

const buildPracticeOrderEditGuideTourEventProperties = (
  event: GuideTourNotificationInteractionEvent,
  component_name?: string
) => {
  const pathname = event.pathname ?? event.route ?? ''
  const screen_name = pathname
    ? pathNameToSegmentScreenName(pathname)
    : ScreenName.Orders

  return {
    component_category: component_name
      ? practiceAnalyticsDict.componentCategory.button
      : null,
    component_name: component_name ?? null,
    modal_name: practiceAnalyticsDict.modalName.tutorialInProgress,
    owner_squad: OwnerSquad.DtcZeOrders,
    screen_name,
    step_current: event.currentStep ?? null,
    step_name: event.step?.title ?? null,
    step_total: event.totalStep ?? null,
    target_id: event.step?.targetId ?? null,
    tour_id: PRACTICE_ORDER_EDIT_TOUR_ID,
    tour_version: event.tourVersion ?? event.tour?.version ?? null,
  }
}

export const trackPracticeOrderEditGuideTourInteraction = (
  event: GuideTourNotificationInteractionEvent
): boolean => {
  if (!isPracticeOrderEditGuideTour(event)) {
    return false
  }

  const trackableEvent = getPracticeOrderEditGuideTourTrackableEvent(event)

  if (!trackableEvent) {
    return false
  }

  void sendAnalyticsEvent(
    trackableEvent.eventName,
    buildPracticeOrderEditGuideTourEventProperties(
      event,
      trackableEvent.component_name
    )
  )

  return true
}

export type { PracticeOrderEditGuideTourTrackableAction }
